import { useEffect, useState } from 'react';

import {
  ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT,
  ZOOM_CHAT_ENGAGEMENT_STARTED_EVENT
} from './zoomChat.constants';
import { onZoomChatSdkAvailable } from './zoomChatSdk';

// A vendor getter that throws reads as no chat: the warning it drives is advice, and the tap-time
// check in escalateToZoomChat still decides what Connect does.
const readIsChatting = (sdk: ZoomCampaignSdk | undefined): boolean => {
  try {
    return Boolean(sdk?.isChatting);
  } catch {
    return false;
  }
};

const ENGAGEMENT_EVENTS = [ZOOM_CHAT_ENGAGEMENT_STARTED_EVENT, ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT];

// How often the getter is read again with no event. A reload during a live chat gets the chat back
// about a second after the runtime reports ready, and nothing is emitted when it does: no
// engagement_started, chat_ready, open or show (stage trace, Nov 3). A read is a property access,
// and an unchanged answer leaves the state as it is.
const REREAD_INTERVAL_MS = 1000;

/**
 * Whether a Zoom chat is live right now, kept current for as long as the caller is mounted.
 *
 * The Connect control reads it to warn, before the tap, that an open Zoom chat will be closed and
 * replaced (LMD-2222), and to say nothing when there is none. The answer is always Zoom's own
 * `isChatting`, the same getter escalateToZoomChat reads at the tap, so the warning and what the tap
 * does cannot disagree. `engagement_started` and `engagement_ended` only say when to read it again,
 * for a chat the patient opens from the corner bubble or one an agent ends while the control is on
 * screen. The event name is not the answer: the runtime replays `engagement_started` when it
 * restores a persisted session (Aug 19 decompile), whether or not a chat is still live. Each event
 * reads the getter at once and again a turn later, in case the vendor updates it after emitting.
 *
 * The read after subscribing is the reconcile the visibility gate does too: the render's read and
 * the subscription, a passive effect run after paint, are two moments, and an engagement that
 * started or ended between them fired its event to nobody. The subscription is all or nothing: if
 * the vendor rejects either event, neither stays, and the warning keeps the steady reads alone
 * rather than being able to move one way only. escalateToZoomChat still checks again at the tap.
 *
 * The steady read (`REREAD_INTERVAL_MS`) is for the change no event announces: a chat the runtime
 * restores after a reload. Without it the slot keeps the bubble hidden with the escalation-only flag
 * on, and Connect offers a new chat with no warning, while the restored one is still live.
 */
export const useZoomChatLiveEngagement = (): boolean => {
  const [isChatting, setIsChatting] = useState(() => readIsChatting(window.zoomCampaignSdk));

  useEffect(() => {
    let detach: () => void = () => {};
    let rereadTimer: number | undefined;
    let steadyReadTimer: number | undefined;

    const stopWaiting = onZoomChatSdkAvailable((sdk) => {
      const read = () => setIsChatting(readIsChatting(sdk));
      const onEngagementEvent = () => {
        read();
        window.clearTimeout(rereadTimer);
        rereadTimer = window.setTimeout(read, 0);
      };
      const subscribed: string[] = [];
      const unsubscribe = () =>
        subscribed.forEach((event) => {
          try {
            sdk.off(event, onEngagementEvent);
          } catch {
            // Unsubscribing is best effort; the state setter is a no-op once unmounted.
          }
        });

      try {
        ENGAGEMENT_EVENTS.forEach((event) => {
          sdk.on(event, onEngagementEvent);
          subscribed.push(event);
        });
        detach = unsubscribe;
      } catch {
        unsubscribe();
      }

      read();
      steadyReadTimer = window.setInterval(read, REREAD_INTERVAL_MS);
    });

    return () => {
      stopWaiting();
      detach();
      window.clearTimeout(rereadTimer);
      window.clearInterval(steadyReadTimer);
    };
  }, []);

  return isChatting;
};
